import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Firm discovery settings (T-0174 API, `apps/api/src/routes/discovery-settings.ts`):
 * how many days a secure link works from when it is sent (Decision 54.26).
 * Any firm user reads; only a firm admin changes it (`can_edit`). A change
 * applies to links sent from then on.
 */

export const LINK_TTL_MIN_DAYS = 1;
export const LINK_TTL_MAX_DAYS = 30;

export const DiscoverySettingsSchema = z.object({
  link_ttl_days: z.number().int(),
  updated_at: z.string().nullable(),
  updated_by_name: z.string().nullable(),
  can_edit: z.boolean(),
});
export type DiscoverySettings = z.infer<typeof DiscoverySettingsSchema>;

const PATH = '/api/v1/discovery-settings';

export const discoverySettingsKeys = { all: ['discovery-settings'] as const };

export function useDiscoverySettingsQuery(enabled = true) {
  return useQuery({
    queryKey: discoverySettingsKeys.all,
    queryFn: () => apiClient(PATH, DiscoverySettingsSchema),
    enabled,
    staleTime: 60_000,
  });
}

export function useUpdateLinkLifetimeMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (days: number) =>
      apiClient(PATH, DiscoverySettingsSchema, { method: 'PUT', body: { link_ttl_days: days } }),
    onSuccess: (data) => qc.setQueryData(discoverySettingsKeys.all, data),
  });
}
